import React from 'react';
import { Link } from 'react-router-dom';
import BlogArticleTemplate from '../../components/BlogArticleTemplate';

const BrokerVsBank = () => (
  <BlogArticleTemplate
    title="Mortgage Broker vs Bank: Which Should You Choose?"
    description="An honest comparison of using a mortgage broker versus going directly to a bank, including costs, access to products, and the pros and cons of each approach."
    path="/blog/broker-vs-bank"
    author="Manage Your Loans Team"
    date="2026-02-12"
    readTime={9}
    faqs={[
      {
        q: 'Do mortgage brokers charge fees?',
        a: 'Most mortgage brokers in Australia do not charge fees to borrowers. Brokers are paid a commission by the lender when your loan settles, typically around 0.5% to 0.7% of the loan amount (upfront) plus a smaller ongoing trail commission. This commission is built into the loan product — you pay the same rate whether you go through a broker or directly to that lender. Some brokers may charge a fee for complex or commercial lending. Always ask about fees upfront.',
      },
      {
        q: 'How many lenders does a mortgage broker have access to?',
        a: 'The number of lenders varies by brokerage. At Manage Your Loans, our brokers access products from 80+ lenders, including major banks, second-tier lenders, credit unions, and non-bank lenders. This breadth of access means we can compare a wide range of products to find the one that best matches your needs, whether you prioritise the lowest rate, specific features, or approval speed.',
      },
      {
        q: 'Is a broker better than going to my bank?',
        a: 'Neither option is universally better — it depends on your situation. A broker offers broader product comparison and can identify lenders you may not have considered. Going directly to your bank may be simpler if you have a strong existing relationship and are confident their product is competitive. However, research consistently shows that borrowers who use brokers tend to achieve more competitive rates because of the comparison process.',
      },
      {
        q: 'Are mortgage brokers regulated in Australia?',
        a: 'Yes, mortgage brokers in Australia are regulated under the National Consumer Credit Protection Act 2009. Brokers must hold an Australian Credit Licence or be an authorised Credit Representative under a licensee. They are required to act in the best interests of the borrower (the Best Interests Duty, introduced in 2021), provide a credit proposal disclosure document, and comply with responsible lending obligations. ASIC oversees compliance.',
      },
    ]}
  >
    <p className="text-lg text-gray-700 leading-relaxed mb-8">
      When it comes to getting a home loan in Australia, you have two main
      paths: go directly to a bank, or use a mortgage broker who compares
      products across multiple lenders on your behalf. Both approaches can
      deliver a good outcome, but they work quite differently. This guide
      offers an honest comparison to help you decide which is right for you.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      What Does a Mortgage Broker Do?
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      A mortgage broker acts as an intermediary between you and multiple
      lenders. Rather than offering one institution's products, a broker
      compares options from a panel of lenders — typically 20 to 60 or more
      — to find a loan that suits your circumstances. The broker manages the
      application process, liaising with the lender on your behalf from
      initial enquiry through to settlement.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Since January 2021, brokers in Australia are legally required to act in
      the best interests of borrowers under the Best Interests Duty. This
      means the broker must prioritise your interests over their own,
      including recommending a product even if it pays a lower commission.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      What Happens When You Go Directly to a Bank?
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      When you approach a bank directly, a bank lending specialist (or mobile
      lender) will present the products available within that institution.
      They cannot recommend products from competing lenders. The bank
      specialist's role is to help you choose the most suitable product
      from their own range and process your application.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Bank lending specialists are not subject to the Best Interests Duty
      that applies to brokers. Their obligation is to ensure the product is
      "not unsuitable" for you, which is a lower threshold than the broker's
      requirement to act in your best interests.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Side-by-Side Comparison
    </h2>
    <div className="overflow-x-auto my-8">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-[#00594C] text-white">
            <th className="px-4 py-3 text-left font-semibold rounded-tl-lg">Factor</th>
            <th className="px-4 py-3 text-left font-semibold">Mortgage Broker</th>
            <th className="px-4 py-3 text-left font-semibold rounded-tr-lg">Bank Direct</th>
          </tr>
        </thead>
        <tbody className="text-gray-700">
          <tr className="border-b border-gray-100">
            <td className="px-4 py-3 font-medium">Product range</td>
            <td className="px-4 py-3">20 to 60+ lenders</td>
            <td className="px-4 py-3">One institution's products</td>
          </tr>
          <tr className="border-b border-gray-100 bg-gray-50">
            <td className="px-4 py-3 font-medium">Cost to you</td>
            <td className="px-4 py-3">Usually no fee (commission from lender)</td>
            <td className="px-4 py-3">No fee</td>
          </tr>
          <tr className="border-b border-gray-100">
            <td className="px-4 py-3 font-medium">Duty</td>
            <td className="px-4 py-3">Best Interests Duty (legally binding)</td>
            <td className="px-4 py-3">"Not unsuitable" obligation</td>
          </tr>
          <tr className="border-b border-gray-100 bg-gray-50">
            <td className="px-4 py-3 font-medium">Rate negotiation</td>
            <td className="px-4 py-3">Compares rates across panel</td>
            <td className="px-4 py-3">May offer discretionary discounts</td>
          </tr>
          <tr className="border-b border-gray-100">
            <td className="px-4 py-3 font-medium">Availability</td>
            <td className="px-4 py-3">Often evenings and weekends</td>
            <td className="px-4 py-3">Typically business hours (branch)</td>
          </tr>
          <tr className="border-b border-gray-100 bg-gray-50">
            <td className="px-4 py-3 font-medium">Ongoing support</td>
            <td className="px-4 py-3">Annual rate reviews common</td>
            <td className="px-4 py-3">Varies by institution</td>
          </tr>
          <tr>
            <td className="px-4 py-3 font-medium rounded-bl-lg">Application process</td>
            <td className="px-4 py-3">Broker manages end-to-end</td>
            <td className="px-4 py-3 rounded-br-lg">You manage with bank support</td>
          </tr>
        </tbody>
      </table>
    </div>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      When a Broker Makes Sense
    </h2>
    <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
      <li className="leading-relaxed">
        <strong>You want to compare options.</strong> If you do not have time to
        visit multiple banks and compare products yourself, a broker does this
        for you across a wide panel.
      </li>
      <li className="leading-relaxed">
        <strong>Your situation is complex.</strong> Self-employed borrowers,
        borrowers with non-standard income, or those purchasing investment
        properties often benefit from a broker's ability to match them with
        the right lender's credit policy.
      </li>
      <li className="leading-relaxed">
        <strong>You are a first home buyer.</strong> Navigating grants, schemes,
        and lender requirements for the first time is significantly easier with
        a broker's guidance.{' '}
        <Link to="/blog/first-home-buyer-guide" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
          Read our first home buyer guide
        </Link>.
      </li>
      <li className="leading-relaxed">
        <strong>You are refinancing.</strong> A broker can quickly assess whether
        switching lenders will save you money after accounting for all costs.{' '}
        <Link to="/blog/when-to-refinance" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
          Learn about when to refinance
        </Link>.
      </li>
      <li className="leading-relaxed">
        <strong>You value convenience.</strong> Brokers often offer evening and
        weekend appointments, phone and video consultations, and digital
        document signing. At Manage Your Loans, all loan documents are signed
        securely via SignAndGo, so you can complete the process without
        visiting a branch.
      </li>
    </ul>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      When Going Direct Makes Sense
    </h2>
    <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
      <li className="leading-relaxed">
        <strong>You have a strong existing relationship.</strong> If you have
        been with your bank for years and they are offering a competitive rate,
        going direct may be simpler. However, it is still worth having a broker
        benchmark the offer against the wider market.
      </li>
      <li className="leading-relaxed">
        <strong>You want a specific product.</strong> If you have already
        researched and identified the exact product you want, and it is only
        available directly from the lender, going direct is the logical choice.
      </li>
      <li className="leading-relaxed">
        <strong>You prefer face-to-face in-branch service.</strong> Some
        borrowers prefer the familiarity of walking into their local branch.
      </li>
    </ul>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      The Cost Question: Who Pays the Broker?
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      Brokers are paid by the lender, not by you. The lender pays the broker
      an upfront commission (typically 0.5% to 0.7% of the loan amount) and a
      smaller ongoing trail commission (typically 0.15% to 0.2% per annum).
      This commission is built into the loan product, meaning you pay the same
      rate whether you go through a broker or apply directly with that lender.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      Under the Best Interests Duty, brokers must disclose their commission
      structure to you and must not let commission influence their recommendation.
      If you have any questions about how your broker is compensated, ask them
      directly — a good broker will be transparent.
    </p>

    <h2 className="text-2xl md:text-3xl font-bold text-[#00594C] mt-12 mb-4">
      Market Trends: Broker Usage in Australia
    </h2>
    <p className="text-gray-700 leading-relaxed mb-4">
      According to industry data, mortgage brokers now facilitate close to
      70% of all new residential home loans in Australia. This share has
      grown steadily over the past decade, reflecting borrower preference for
      the comparison, convenience, and advocacy that brokers provide.
    </p>
    <p className="text-gray-700 leading-relaxed mb-4">
      If you would like to experience the broker process firsthand,{' '}
      <Link to="/contact" className="text-[#00594C] font-semibold underline hover:text-[#99DF63] transition-colors">
        book a consultation
      </Link>{' '}
      with our team. We will compare your current loan (or your goals for a
      new loan) against the products available across our panel of 80+
      lenders — at no cost and with no obligation.
    </p>
  </BlogArticleTemplate>
);

export default BrokerVsBank;
